// src/hooks/useActionBoard.js

import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

// Import actions and thunks from actionAreasSlice
import {
    createActionArea,
    updateActionArea,
    removeActionArea,
    fetchActionAreas,
    saveActionArea,
} from '../features/actionAreasSlice';
import { actionAreasSelectors } from '../features/actionAreasSlice';

// Import actions and thunks from measuresSlice
import {
    addMeasure,
    updateMeasure,
    removeMeasure,
    createMeasure,
    saveMeasure,
    fetchMeasures,
} from '../features/measuresSlice';
import { measuresSelectors } from '../features/measuresSlice';

// Import selector for unitId
import { selectSelectedUnitIds } from '../features/unitsSlice';

export const useActionBoard = () => {
    const dispatch = useDispatch();

    // State for tracking newly created action area
    const [newActionAreaId, setNewActionAreaId] = useState(null);
    const [isAddingActionArea, setIsAddingActionArea] = useState(false);

    // Select all selected units (primary is first)
    const selectedUnitIds = useSelector(selectSelectedUnitIds);

    // Select Action Areas using selectors from actionAreasSlice
    const allActionAreas = useSelector(actionAreasSelectors.selectAll);
    const actionAreasStatusByUnit = useSelector((state) => state.actionAreas.statusByUnit || {});
    const actionAreasErrorByUnit = useSelector((state) => state.actionAreas.errorByUnit || {});

    // Select Measures using selectors from measuresSlice
    const measures = useSelector(measuresSelectors.selectAll);
    const measuresStatus = useSelector((state) => state.measures.status);
    const measuresError = useSelector((state) => state.measures.error);

    // Select actionAreaStatuses from measuresSlice to prevent redundant fetches
    const actionAreaStatuses = useSelector((state) => state.measures.actionAreaStatuses);

    // Select save statuses and errors for Action Areas
    const saveActionAreaStatus = useSelector((state) => state.actionAreas.saveStatus);
    const saveActionAreaError = useSelector((state) => state.actionAreas.saveError);

    // Select save statuses and errors for Measures
    const saveMeasureStatus = useSelector((state) => state.measures.saveStatus);
    const saveMeasureError = useSelector((state) => state.measures.saveError);

    // Select create measure statuses and errors
    const createMeasureStatus = useSelector((state) => state.measures.createStatus);
    const createMeasureError = useSelector((state) => state.measures.createError);

    const createActionAreaStatus = useSelector((state) => state.actionAreas.createStatus);
    const createActionAreaError = useSelector((state) => state.actionAreas.createError);

    // Fetch Action Areas for all selected units
    useEffect(() => {
        selectedUnitIds.forEach((id) => {
            const status = actionAreasStatusByUnit[id];
            if (!status || status === 'idle' || status === 'failed') {
                dispatch(fetchActionAreas(id));
            }
        });
    }, [selectedUnitIds, dispatch, actionAreasStatusByUnit]);

    // After Action Areas are successfully fetched, fetch Measures for each Action Area
    useEffect(() => {
        allActionAreas.forEach((actionArea) => {
            if (!selectedUnitIds.includes(actionArea.unit)) return;
            const status = actionAreaStatuses[actionArea.id];
            // Treat undefined as 'idle'
            if (!status || status === 'idle') {
                dispatch(fetchMeasures({ actionAreaId: actionArea.id, unitId: actionArea.unit }));
            }
        });
    }, [allActionAreas, actionAreaStatuses, dispatch, selectedUnitIds]);

    // Handlers for Action Areas
    const handleAddActionArea = async (unitId) => {
        setIsAddingActionArea(true);
        try {
            const result = await dispatch(createActionArea(unitId)).unwrap();
            setNewActionAreaId(result.id);
            return result;
        } catch (error) {
            console.error('Error adding action area:', error);
            throw error;
        } finally {
            setIsAddingActionArea(false);
        }
    };
    const handleUpdateActionArea = (actionAreaId, newData) =>
        dispatch(updateActionArea({ id: actionAreaId, changes: newData }));
    const handleRemoveActionArea = (actionAreaId) => dispatch(removeActionArea(actionAreaId));

    // Modified save handler to handle both creating and updating
    const handleSaveActionArea = (actionArea) => dispatch(saveActionArea(actionArea));

    // Handlers for Measures
    const handleAddMeasure = (measureData) => dispatch(addMeasure(measureData));
    const handleUpdateMeasure = (measureId, newData) =>
        dispatch(updateMeasure({ id: measureId, changes: newData }));
    const handleRemoveMeasure = (measureId) => dispatch(removeMeasure(measureId));
    const handleSaveMeasure = (measure) => dispatch(saveMeasure(measure));

    /**
     * Corrected Create Measure Handler
     * Accepts both actionAreaId and measure, ensuring all required data is provided.
     */
    const handleCreateMeasure = (actionAreaId, measure) => {
        dispatch(createMeasure({ actionAreaId, measure }))
            .unwrap()
            .then((createdMeasure) => {
                console.log('Measure created:', createdMeasure);
            })
            .catch((error) => {
                console.error('Failed to create measure:', error);
            });
    };

    // Combine Action Areas with their Measures and filter by selected units
    const combinedActionAreas = useMemo(() => {
        return allActionAreas
            .filter((actionArea) => selectedUnitIds.includes(actionArea.unit))
            .map((actionArea) => ({
                ...actionArea,
                measures: measures.filter((measure) => measure.actionarea === actionArea.id),
            }));
    }, [allActionAreas, measures, selectedUnitIds]);

    // Aggregate status and error for selected units
    const aggregatedStatus = useMemo(() => {
        if (!selectedUnitIds.length) return 'idle';
        if (selectedUnitIds.some((id) => actionAreasStatusByUnit[id] === 'loading')) return 'loading';
        if (selectedUnitIds.some((id) => actionAreasStatusByUnit[id] === 'failed')) return 'failed';
        if (selectedUnitIds.every((id) => actionAreasStatusByUnit[id] === 'succeeded')) return 'succeeded';
        return 'idle';
    }, [selectedUnitIds, actionAreasStatusByUnit]);

    const aggregatedError = useMemo(() => {
        const firstErrorUnit = selectedUnitIds.find((id) => actionAreasStatusByUnit[id] === 'failed');
        return firstErrorUnit ? actionAreasErrorByUnit[firstErrorUnit] : null;
    }, [selectedUnitIds, actionAreasStatusByUnit, actionAreasErrorByUnit]);

    return {
        // Combined Action Areas Data and Status
        actionAreas: combinedActionAreas,
        status: aggregatedStatus,
        error: aggregatedError,

        // State management for new action areas
        newActionAreaId,
        setNewActionAreaId,
        isAddingActionArea,

        // Measures Status (optional, can be used for loading indicators)
        measuresStatus,
        measuresError,

        // Save Statuses and Errors for Action Areas
        saveStatus: saveActionAreaStatus,
        saveError: saveActionAreaError,

        // Save Statuses and Errors for Measures
        saveMeasureStatus,
        saveMeasureError,

        // Create Measure Statuses and Errors
        createMeasureStatus,
        createMeasureError,

        createActionAreaStatus,
        createActionAreaError,

        // Handlers

        handleAddActionArea,
        addActionArea: handleAddActionArea,
        updateActionArea: handleUpdateActionArea,
        removeActionArea: handleRemoveActionArea,
        saveActionArea: handleSaveActionArea,

        addMeasure: handleAddMeasure,
        updateMeasure: handleUpdateMeasure,
        removeMeasure: handleRemoveMeasure,
        saveMeasure: handleSaveMeasure,
        createMeasure: handleCreateMeasure,
    };
};
